import * as React from 'react';
import AppTabs from '../../../components/AppTabs';
import { FC, useState, useCallback, useEffect } from 'react';
import { ToggleButton, ToggleButtonGroup } from '@mui/material';
import { Modal, ModalHeader, ModalBody, ModalFooter, Form } from 'reactstrap';
import { useTranslations } from '../../../store/translations/hooks';
import { TK } from '../../../store/translations/translationKeys';
import ButtonsContainer from '../../../components/ButtonsContainer';
import { TextInputField, AutocompleteTextField, NumberInputField, ComboField } from '../../../components/Forms/fields';
import { Row } from '../../../components/Forms/table/Row';
import { Col } from '../../../components/Forms/table/Column';
import { PackSettings, RecurrenceFrequency, FeatureEnum } from '../../../models/api-models';
import ToolTip from '../../../components/ToolTip';
import { useSelector } from 'react-redux';
import { useGeneralSettings, useServicesSet } from '../../../store/settings/hooks';
import { asSnake } from '../../../shared/string';
import { Tabs, Tab } from '@material-ui/core';
import InvoiceItemDetailsForm from '../../../components/Forms/InvoiceItemDetailsForm/InvoiceItemDetailsForm';
import { InfoBox } from '../../../components/UI/InfoBox';
import { getFeatures } from '../../../store/session/selectors';
import { serviceId, serviceName } from '../../../shared/appointmint/appointment';
import { DEFAULT_PACK_TOTAL_CREDITS, DEFAULT_PACK_TOTAL_MINUTES, withDefaultCreditsForAppointmentTypes } from './packDefaults';
import AppButton from '../../../components/AppButton';

export interface PackConfigurationModalProps {
    defaultValue?: PackSettings,
    onClose?: () => void,
    onSave: (value: PackSettings, applyToAllVersions?: boolean) => void,
    onDelete: (id: string) => void,
}

const PackConfigurationModal : FC<PackConfigurationModalProps> = ({
    defaultValue,
    onClose,
    onSave,
    onDelete,
}) => {

    const t = useTranslations();
    const features = useSelector(getFeatures);
    const hasInvoicesFeature = features.includes(FeatureEnum.Invoices);
    const settings = useGeneralSettings();

    const allAppointmentTypes = useServicesSet()

    const [tab, setTab] = useState<'settings' | 'invoices'>('settings');
    const [showUpdateModeModal, setShowUpdateModeModal] = useState(false);
    const [pack, setPack] = useState<PackSettings>({id: '', name: '', creationDate: '', version: 0, price: 0, appointmentTypes: [], invoiceItemDetails: undefined, ...defaultValue});

    useEffect(() => {
        if (!defaultValue) return;
        setPack(defaultValue);
        setShowUpdateModeModal(false);
    }, [defaultValue]);

    const updateName = useCallback((name: string) => setPack(prev => ({...prev, name})), [setPack]);
    const updateTotalCredits = useCallback((totalCredits: string) => setPack(prev => ({...prev, totalCredits: Number(totalCredits)})), [setPack])
    const updatePrice = useCallback((price?: number) => setPack(prev => ({...prev, price: price ?? 0})), [setPack]);
    const updateAppointmentTypes = useCallback((appointmentTypes: string[]) => setPack(prev => ({
        ...prev as PackSettings,
        appointmentTypes,
        ...(prev.totalCredits !== undefined
            ? { appointmentTypesCredits: withDefaultCreditsForAppointmentTypes(appointmentTypes, prev.appointmentTypesCredits) }
            : {}),
    })), [setPack]);
    const updateValidity = useCallback((validityMonths?: string) => setPack(prev => ({
        ...prev, 
        validityDays: validityMonths ? Number(validityMonths)*30 : undefined,
    })), [setPack])

    const byTimeClick = useCallback(() => setPack(prev => prev.totalMinutes !== undefined ? prev : {
        ...prev,
        totalMinutes: DEFAULT_PACK_TOTAL_MINUTES,
        totalCredits: undefined,
        subscriptionUnit: undefined,
        subscriptionPeriod: undefined,
    }), [setPack])
    const byCreditsClick = useCallback(() => setPack(prev => prev.totalCredits !== undefined ? prev : {
        ...prev,
        totalCredits: DEFAULT_PACK_TOTAL_CREDITS,
        totalMinutes: undefined,
        subscriptionUnit: undefined,
        subscriptionPeriod: undefined,
        appointmentTypesCredits: withDefaultCreditsForAppointmentTypes(prev.appointmentTypes, prev.appointmentTypesCredits),
    }), [setPack])
    const bySubscriptionClick = useCallback(() => setPack(prev => prev.subscriptionUnit !== undefined ? prev : {...prev, subscriptionUnit: RecurrenceFrequency.Monthly, subscriptionPeriod: 1, totalCredits: undefined, totalMinutes: undefined}), [setPack])

    const updateCreditsForType = useCallback((type: string) => (credits?: number) => setPack(prev => ({
        ...prev,
        appointmentTypesCredits: {
            ...prev.appointmentTypesCredits,
            [type]: credits ?? 0,
        },
    })), [setPack])
   
    const subscriptionUnitMap = {
        [t(TK.days)]: RecurrenceFrequency.Daily,
        [t(TK.weeks)]: RecurrenceFrequency.Weekly,
        [t(TK.months)]: RecurrenceFrequency.Monthly,
        [t(TK.day)]: RecurrenceFrequency.Daily,
        [t(TK.week)]: RecurrenceFrequency.Weekly,
        [t(TK.month)]: RecurrenceFrequency.Monthly,
        [RecurrenceFrequency.Daily]: (pack.subscriptionPeriod ?? 0) > 1 ? t(TK.days) : t(TK.day),
        [RecurrenceFrequency.Weekly]: (pack.subscriptionPeriod ?? 0) > 1 ? t(TK.weeks) : t(TK.week),
        [RecurrenceFrequency.Monthly]: (pack.subscriptionPeriod ?? 0) > 1 ? t(TK.months) : t(TK.month),
    }

    const updateSubscriptionPeriod = useCallback((val?: number) => setPack(prev => ({...prev, subscriptionPeriod: val ? val : 1})), [setPack])
    const updateSubscriptionUnit = useCallback((unit?: string) => setPack(prev => ({
        ...prev, 
        subscriptionUnit: subscriptionUnitMap[unit ?? t(TK.months)] as RecurrenceFrequency,
    })), [setPack])

    const isTimePack = pack.totalMinutes !== undefined;
    const isCreditsPack = pack.totalCredits !== undefined;
    const isSubscriptionPack = pack.subscriptionUnit !== undefined;
    const selectedPackType = isTimePack
        ? 'time'
        : isCreditsPack
            ? 'credits'
            : 'subscription';
    const selectedPackDescription = isTimePack
        ? t(TK.packByTimeDescription)
        : isCreditsPack
            ? t(TK.packByCreditsDescription)
            : t(TK.packBySubscriptionDescription);
    const canSavePack = !!pack.name?.length && (
        (pack.totalMinutes ?? 0) > 0
        || (pack.totalCredits ?? 0) > 0
        || selectedPackType === 'subscription'
    );
    const isEditingExistingTemplate = !!defaultValue?.templateId;

    const handleSaveClick = useCallback(() => {
        if (!canSavePack) return;

        if (!isEditingExistingTemplate) {
            onSave(pack, false);
            return;
        }

        setShowUpdateModeModal(true);
    }, [canSavePack, isEditingExistingTemplate, onSave, pack]);


    return (
        <Modal size="lg" isOpen={!!defaultValue}>
            <ModalHeader toggle={onClose}>{t(TK.packConfiguration)} - {pack?.name || t(TK.new)} (v{pack?.version || '1'})</ModalHeader>
            <ModalBody>
                { pack &&
                    <>
                        <AppTabs value={tab} onChange={(_, v) => setTab(v)} indicatorColor="primary" textColor="primary" style={{ marginBottom: 'var(--spacer-S)', borderBottom: 'var(--border-thin) solid var(--appLigthGrey)' }}>
                            <Tab label={t(TK.settings)} value="settings" />
                            {hasInvoicesFeature && (
                                <Tab label={t(TK.invoiceData)} value="invoices" />
                            )}
                        </AppTabs>

                        <div>
                            {tab === 'settings' && (
                                <Form style={{display: 'flex', marginTop: 'var(--spacer-S)', flexDirection: 'column', gap: 'var(--spacer-M)'}}>
                                    <div>
                                        <ToggleButtonGroup
                                            value={selectedPackType}
                                            exclusive
                                            fullWidth
                                            sx={{
                                                backgroundColor: 'var(--surface-brand-soft)',
                                                border: 'var(--border-thin) solid var(--surface-border-subtle)',
                                                borderRadius: '14px',
                                                padding: 'var(--spacer-XXS)',
                                                gap: 'var(--spacer-XXS)',
                                                '& .MuiToggleButtonGroup-grouped': {
                                                    border: 0,
                                                    borderRadius: '10px !important',
                                                },
                                                '& .MuiToggleButton-root': {
                                                    flex: 1,
                                                    backgroundColor: 'var(--brand-accent)',
                                                    color: 'var(--brand-secondary)',
                                                    textTransform: 'none',
                                                    fontSize: '0.84rem',
                                                    fontWeight: 600,
                                                    minHeight: '40px',
                                                    padding: 'var(--spacer-XS) var(--spacer-S)',
                                                    transition: 'all 0.2s ease',
                                                    '&:hover': {
                                                        backgroundColor: 'var(--surface-brand-soft)',
                                                    },
                                                    '&.Mui-selected': {
                                                        background: 'var(--brand-secondary)',
                                                        color: 'var(--brand-accent)',
                                                        boxShadow: '0 4px 12px var(--brand-secondary-glow)',
                                                        '&:hover': {
                                                            background: 'var(--brand-secondary)',
                                                        },
                                                    },
                                                },
                                            }}
                                        >
                                            <ToggleButton id='by-time-button' value='time' onClick={byTimeClick}>
                                                {t(TK.pack)} ({t(TK.byTime)})
                                            </ToggleButton>
                                            <ToggleButton id='by-credits-button' value='credits' onClick={byCreditsClick}>
                                                {t(TK.pack)} ({t(TK.byCredits)})
                                            </ToggleButton>
                                            <ToggleButton id='by-subscription' value='subscription' onClick={bySubscriptionClick}>
                                                {t(TK.subscription)}
                                            </ToggleButton>
                                        </ToggleButtonGroup>

                                        <ToolTip target='by-time-button' toolTipContent={t(TK.packByTimeDescription)}/>
                                        <ToolTip target='by-credits-button' toolTipContent={t(TK.packByCreditsDescription)}/>
                                        <ToolTip target='by-subscription' toolTipContent={t(TK.packBySubscriptionDescription)}/>
                                    </div>

                                    <InfoBox
                                        style={{
                                            marginBottom: 0,
                                            backgroundColor: 'var(--surface-brand-soft)',
                                            borderLeft: 'var(--border-thick) solid var(--brand-secondary)',
                                        }}
                                    >
                                        <small>{selectedPackDescription}</small>
                                    </InfoBox>

                                    <Row>
                                        <Col md={12}>
                                            <TextInputField
                                                id={'pack-name'}
                                                label={t(TK.name)}
                                                value={pack.name}
                                                onChange={updateName}
                                            />
                                        </Col>
                                    </Row>
                                    <Row>
                                        <Col md={12}>
                                            <AutocompleteTextField
                                                id={'pack-available-for'}
                                                label={t(TK.availableFor)}
                                                multiSelect
                                                options={allAppointmentTypes.map(s => serviceName(s, settings))}
                                                values={pack.appointmentTypes.map(s => serviceName(s, settings))}
                                                onValuesChanged={values => updateAppointmentTypes(values.map(s => serviceId(s, settings)))}
                                            />
                                        </Col>
                                    </Row>
                                    <Row>
                                        <Col md={6}>
                                            <TextInputField
                                                id={'pack-validity-months'}
                                                label={t(TK.validityMonths)}
                                                type='number'
                                                value={pack.validityDays ? (pack.validityDays / 30).toString() : undefined}
                                                onChange={updateValidity}
                                            />
                                        </Col>
                                        <Col md={6}>
                                            <NumberInputField
                                                id={'pack-price'}
                                                label={t(TK.price)}
                                                prepend="€"
                                                value={pack.price}
                                                type='number'
                                                onChange={updatePrice}
                                            />
                                        </Col>
                                    </Row>

                                    {isTimePack &&
                                        <Row>
                                            <Col md={5} xl={3} style={{display: 'flex', gap: 'var(--spacer-XS)'}}>
                                                <NumberInputField
                                                    id={'pack-duration-hours'}
                                                    label={t(TK.duration)}
                                                    append="h"
                                                    value={Math.floor((pack.totalMinutes ?? 0)/60)}
                                                    min={0}
                                                    onChange={hours => setPack({...pack, totalMinutes: ((hours ?? 0)*60 + (pack.totalMinutes ?? 0)%60)})}
                                                />
                                                <NumberInputField
                                                    id={'pack-duration-minutes'}
                                                    append="m"
                                                    value={(pack.totalMinutes ?? 0)%60}
                                                    min={0}
                                                    onChange={minutes => setPack({...pack, totalMinutes: Math.floor((pack.totalMinutes ?? 0)/60)*60 + (minutes ?? 0)})}
                                                />
                                            </Col>
                                        </Row>
                                    }
                                    {isCreditsPack &&
                                        <Row>
                                            <Col xl={2}>
                                                <TextInputField
                                                    id={'pack-total-credits'}
                                                    label={t(TK.totalCredits)}
                                                    value={pack.totalCredits}
                                                    type='number'
                                                    min={1}
                                                    step={1}
                                                    onChange={updateTotalCredits}
                                                />
                                            </Col>
                                        </Row>
                                    }
                                    {isCreditsPack &&
                                        <Row>
                                            {pack.appointmentTypes.map(type => (
                                                <Col xl={4} key={type}>
                                                    <NumberInputField
                                                        id={`pack-credits-per-${asSnake(type)}`}
                                                        label={`${t(TK.creditsPer)} ${type}`}
                                                        value={pack.appointmentTypesCredits ? pack.appointmentTypesCredits[type] : undefined}
                                                        type='number'
                                                        min={0}
                                                        max={pack.totalCredits}
                                                        onChange={updateCreditsForType(type)}
                                                    />
                                                </Col>
                                            ))}
                                        </Row>
                                    }
                                    {pack.subscriptionPeriod !== undefined && pack.subscriptionUnit !== undefined &&
                                        <Row>
                                            <Col style={{position: 'relative', top: '-10px', height: 0}}>
                                                <span style={{color: 'rgba(0, 0, 0, 0.6)', fontSize: '0.9rem'}}>{t(TK.paymentPeriod)}</span>
                                            </Col>
                                            <Col sm={2}>
                                                <NumberInputField
                                                    min={1}
                                                    value={pack.subscriptionPeriod}
                                                    onChange={updateSubscriptionPeriod}
                                                />
                                            </Col>
                                            <Col sm={3}>
                                                <ComboField
                                                    options={Object.values(subscriptionUnitMap).filter(t => typeof t === 'string')}
                                                    value={subscriptionUnitMap[pack.subscriptionUnit]}
                                                    onChange={updateSubscriptionUnit}
                                                />
                                            </Col>
                                        </Row>
                                    }
                                </Form>
                            )}

                            {hasInvoicesFeature && tab === 'invoices' && (
                                <>
                                    <InfoBox>
                                        <small>{t(TK.invoiceDataDisclaimer)}</small>
                                    </InfoBox>
                                    <InvoiceItemDetailsForm
                                        id="pack-invoice-item-details"
                                        value={pack.invoiceItemDetails}
                                        onChange={invoiceItemDetails => setPack(d => ({ ...d, invoiceItemDetails: invoiceItemDetails ?? undefined }))}
                                        parentContext="service"
                                        parentName={pack.name}
                                    />
                                </>
                            )}
                        </div>
                    </>
                }

                <hr/>

                <ButtonsContainer>
                    <AppButton id={'pack-delete-button'} color="danger" onClick={() => onDelete(pack.id)}>{t(TK.delete)}</AppButton>
                    <AppButton id={'pack-save-button'} disabled={!canSavePack} color="success" onClick={handleSaveClick}>{t(TK.save)}</AppButton>
                    <AppButton id={'pack-cancel-button'} onClick={onClose}>{t(TK.cancel)}</AppButton>
                </ButtonsContainer>
            </ModalBody>

            <Modal isOpen={showUpdateModeModal} toggle={() => setShowUpdateModeModal(false)}>
                <ModalHeader toggle={() => setShowUpdateModeModal(false)}>{t(TK.updatePackVersionsTitle)}</ModalHeader>
                <ModalBody>
                    <p style={{marginBottom: 'var(--spacer-M)'}}>{t(TK.updatePackVersionsDescription)}</p>
                    <div style={{display: 'flex', flexDirection: 'column', gap: 'var(--spacer-S)'}}>
                        <AppButton id='pack-save-new-version-button' color="secondary" onClick={() => onSave(pack, false)} style={{textAlign: 'left', whiteSpace: 'normal'}}>
                            <div><b>{t(TK.updateLatestVersionOnly)}</b></div>
                            <small>{t(TK.updateLatestVersionOnlyDescription)}</small>
                        </AppButton>
                        <AppButton id='pack-save-all-versions-button' color="warning" onClick={() => onSave(pack, true)} style={{textAlign: 'left', whiteSpace: 'normal'}}>
                            <div><b>{t(TK.updateAllVersions)}</b></div>
                            <small>{t(TK.updateAllVersionsDescription)}</small>
                        </AppButton>
                    </div>
                    <InfoBox
                        style={{
                            marginTop: 'var(--spacer-S)',
                            marginBottom: 0,
                            backgroundColor: 'var(--warning-bg)',
                            borderLeft: 'var(--border-thick) solid var(--warning-color)',
                        }}
                    >
                        <small>{t(TK.updatePackAllVersionsDisclaimer)}</small>
                    </InfoBox>
                </ModalBody>
                <ModalFooter>
                    <AppButton id='pack-save-mode-cancel-button' onClick={() => setShowUpdateModeModal(false)}>
                        {t(TK.cancel)}
                    </AppButton>
                </ModalFooter>
            </Modal>
        </Modal>
    );
}

export default PackConfigurationModal;
